import { Component, Input, Output, EventEmitter, ViewChild, ElementRef, OnInit, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FormsModule } from '@angular/forms';

export interface LayoutOption {
  id: string;
  label: string;
  icon: string;
  tooltip: string;
}

export interface SearchOptions {
  term: string;
  filterMode: boolean;
}

@Component({
  selector: 'app-layout-switcher',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatIconModule, MatMenuModule, MatTooltipModule, FormsModule],
  templateUrl: './layout-switcher.component.html',
  styleUrls: ['./layout-switcher.component.css']
})
export class LayoutSwitcherComponent implements OnInit, OnChanges {
  @Input() isCollapsed = false;
  @Input() isPaneOne_Collapsed = false;
  @Input() isPaneTwo_Collapsed = false;
  @Input() showSingleTree = false;
  @Input() hideSearch = false;
  @Input() isMobileView = false;

  @Output() layoutChange = new EventEmitter<string>();
  @Output() searchTermChange = new EventEmitter<SearchOptions>();

  @ViewChild('searchInput') searchInput!: ElementRef;

  layoutOptions: LayoutOption[] = [
    {
      id: 'default',
      label: '2-Column',
      icon: 'view_quilt',
      tooltip: 'Default view with both panes'
    },
    {
      id: 'single_tree',
      label: '1-Column',
      icon: 'account_tree',
      tooltip: 'Show full tree in single pane'
    },
    {
      id: 'sidebar',
      label: 'Sidebar',
      icon: 'view_sidebar',
      tooltip: 'Hide navigation pane'
    },
    {
      id: 'collapsed',
      label: 'Collapsed',
      icon: 'view_compact',
      tooltip: 'Collapse to minimal sidebar'
    }
  ];

  currentLayout = 'default';
  showSearch = false;
  searchTerm = '';
  filterMode = false;
  searchTimeout: any = null;

  ngOnInit(): void {
    // Initialize component
    console.log('LayoutSwitcher initialized with showSingleTree:', this.showSingleTree);
  }

  // Add ngOnChanges to detect when showSingleTree input changes
  ngOnChanges(changes: any): void {
    // If we've switched to single tree mode and this was triggered by a search action
    // automatically open the search box
    if (changes.showSingleTree && changes.showSingleTree.currentValue === true) {
      console.log('LayoutSwitcher: Detected switch to single tree mode');

      // We know we got here from a search action if we have a lastSearchAction timestamp
      if (this._lastSearchActionTime && (Date.now() - this._lastSearchActionTime) < 500) {
        console.log('LayoutSwitcher: Opening search box after layout change');
        this.showSearch = true;
        this.focusSearchInput();
        // Clear the timestamp so we don't re-open search on other changes
        this._lastSearchActionTime = 0;
      }
    }
  }

  // Track when search was requested
  private _lastSearchActionTime = 0;

  constructor() {}

  selectLayout(layoutId: string): void {
    // In mobile view, toggle the mobile navigation instead of selecting a layout
    if (this.isMobileView) {
      // Emit a special event to toggle mobile navigation
      this.layoutChange.emit('toggle_mobile_nav');
    } else {
      this.currentLayout = layoutId;
      this.layoutChange.emit(layoutId);
    }
  }

  getActiveIcon(): string {
    // Determine the current layout based on the state of the panes
    if (this.showSingleTree) {
      return 'account_tree';
    } else if (this.isCollapsed) {
      return 'view_compact';
    } else if (this.isPaneOne_Collapsed) {
      return 'view_sidebar';
    } else if (this.isPaneTwo_Collapsed) {
      return 'fullscreen';
    } else {
      return 'view_quilt';
    }
  }

  getActiveTooltip(): string {
    if (this.showSingleTree) {
      return 'Single tree view';
    } else if (this.isCollapsed) {
      return 'Collapsed view';
    } else if (this.isPaneOne_Collapsed) {
      return 'Sidebar view';
    } else if (this.isPaneTwo_Collapsed) {
      return 'Full screen view';
    } else {
      return '2-Column view';
    }
  }

  // Search functionality
  toggleSearch(): void {
    // Record the time when search is toggled
    this._lastSearchActionTime = Date.now();

    // If not already showing search and not already in single tree mode,
    // switch to single tree mode first
    if (!this.showSearch && !this.showSingleTree) {
      console.log('LayoutSwitcher: Switching to single tree mode for search');
      this.layoutChange.emit('single_tree');

      // Note: We don't need to set showSearch here any more as ngOnChanges will handle it
      // when the parent component updates the showSingleTree property
    } else {
      // Already in proper mode, just toggle search visibility
      this.showSearch = !this.showSearch;

      if (this.showSearch) {
        this.focusSearchInput();
      } else {
        // Clear search when closing
        this.clearSearch();
      }
    }
  }

  // Helper method to focus the search input
  private focusSearchInput(): void {
    // Focus the search input after it's displayed
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.nativeElement.focus();
      }
    }, 0);
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.searchTermChange.emit({ term: '', filterMode: this.filterMode });

    // Optional: if we're clearing search and no longer using search,
    // we could potentially switch back to the original layout here
    // But we'll leave that as a UX decision for now
  }

  toggleFilterMode(): void {
    this.filterMode = !this.filterMode;
    console.log(`Filter mode ${this.filterMode ? 'enabled' : 'disabled'}`);

    // Record the time of the search-related action
    this._lastSearchActionTime = Date.now();

    // Make sure we're in single tree mode when toggling filter
    if (!this.showSingleTree) {
      console.log('LayoutSwitcher: Switching to single tree mode for filtering');
      this.layoutChange.emit('single_tree');

      // Note: We don't need the timeout anymore as ngOnChanges will handle showing search
      // and the parent component will trigger the search once in single tree mode
    } else {
      // Already in single tree mode, just apply search with new filter
      if (this.searchTerm.trim()) {
        this.performSearch();
      }
    }
  }

  onSearchKeyUp(event: KeyboardEvent): void {
    // Don't trigger search on Enter key as it has its own handler
    if (event.key === 'Enter') {
      return;
    }

    // Clear existing timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    // Set new timeout for search (500ms delay)
    this.searchTimeout = setTimeout(() => {
      this.performSearch();
    }, 500);
  }

  performSearch(): void {
    // Clear any pending timeout
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    console.log('LayoutSwitcher: performSearch() called with term:', this.searchTerm, 'filterMode:', this.filterMode);

    // Record the time of the search action
    this._lastSearchActionTime = Date.now();

    // Make sure we're in single tree mode when performing search
    if (!this.showSingleTree) {
      console.log('LayoutSwitcher: Switching to single tree mode for search');
      this.layoutChange.emit('single_tree');

      // Note: We don't need the timeout anymore as ngOnChanges will handle this
      // and the parent component will apply the search once in single tree mode
    } else {
      // Already in single tree mode, just emit search
      // Only emit if there's a search term
      if (this.searchTerm.trim()) {
        console.log('LayoutSwitcher: Emitting searchTermChange event with term:', this.searchTerm.trim());
        this.searchTermChange.emit({
          term: this.searchTerm.trim(),
          filterMode: this.filterMode
        });
      } else {
        this.clearSearch();
      }
    }
  }
}
